/**
 * GIS-Style Groundwater Contour Generator
 *
 * Generates smooth potentiometric surface contour lines from well
 * water elevation data using IDW interpolation + marching squares.
 * Used by both 2D Leaflet map and 3D Three.js view.
 */

export interface WellElevation {
  lat: number;
  lon: number;
  waterElev: number;
}

export interface ContourLine {
  elevation: number;
  points: [number, number][]; // [lat, lon] pairs
}

/**
 * IDW interpolation at a grid point from well data.
 */
function idw(
  x: number, y: number,
  wells: { x: number; y: number; val: number }[],
  power: number = 2,
  smoothing: number = 0.0001,
): number {
  let wSum = 0, vSum = 0;
  for (const w of wells) {
    const d = Math.sqrt((x - w.x) ** 2 + (y - w.y) ** 2);
    const dist = Math.max(d, smoothing);
    const weight = 1 / dist ** power;
    wSum += weight;
    vSum += weight * w.val;
  }
  return wSum > 0 ? vSum / wSum : 0;
}

/**
 * Marching squares contour extraction from a 2D grid.
 * Returns polyline segments for a given isolevel.
 */
function marchingSquaresLevel(
  grid: number[][],
  rows: number, cols: number,
  latArr: number[], lonArr: number[],
  level: number,
): [number, number][] {
  const points: [number, number][] = [];

  for (let i = 0; i < rows - 1; i++) {
    for (let j = 0; j < cols - 1; j++) {
      const v00 = grid[i][j];
      const v10 = grid[i + 1][j];
      const v11 = grid[i + 1][j + 1];
      const v01 = grid[i][j + 1];

      // Binary classification
      const code =
        (v00 >= level ? 8 : 0) |
        (v10 >= level ? 4 : 0) |
        (v11 >= level ? 2 : 0) |
        (v01 >= level ? 1 : 0);

      if (code === 0 || code === 15) continue;

      // Linear interpolation along edges
      const lerp = (a: number, b: number, va: number, vb: number) => {
        const t = Math.abs(vb - va) > 1e-10 ? (level - va) / (vb - va) : 0.5;
        return a + t * (b - a);
      };

      // Edge midpoints
      const top: [number, number] = [latArr[i], lerp(lonArr[j], lonArr[j + 1], v00, v01)];
      const bottom: [number, number] = [latArr[i + 1], lerp(lonArr[j], lonArr[j + 1], v10, v11)];
      const left: [number, number] = [lerp(latArr[i], latArr[i + 1], v00, v10), lonArr[j]];
      const right: [number, number] = [lerp(latArr[i], latArr[i + 1], v01, v11), lonArr[j + 1]];

      // Add crossing points based on marching squares case
      const addSegment = (a: [number, number], b: [number, number]) => {
        points.push(a, b);
      };

      switch (code) {
        case 1: case 14: addSegment(top, right); break;
        case 2: case 13: addSegment(right, bottom); break;
        case 3: case 12: addSegment(top, bottom); break;
        case 4: case 11: addSegment(left, bottom); break;
        case 5: addSegment(top, left); addSegment(right, bottom); break;
        case 6: case 9: addSegment(left, right); break;
        case 7: case 8: addSegment(top, left); break;
        case 10: addSegment(top, right); addSegment(left, bottom); break;
      }
    }
  }

  return points;
}

/**
 * Sort contour segments into connected polylines.
 */
function connectSegments(segments: [number, number][]): [number, number][][] {
  if (segments.length < 2) return [];

  const lines: [number, number][][] = [];
  const used = new Set<number>();
  const eps = 1e-7;

  const near = (a: [number, number], b: [number, number]) =>
    Math.abs(a[0] - b[0]) < eps && Math.abs(a[1] - b[1]) < eps;

  // Segments come in pairs (start, end)
  for (let i = 0; i < segments.length; i += 2) {
    if (used.has(i)) continue;
    used.add(i);

    const line: [number, number][] = [segments[i], segments[i + 1]];

    // Try to extend the line by finding connected segments
    let extended = true;
    while (extended) {
      extended = false;
      for (let j = 0; j < segments.length; j += 2) {
        if (used.has(j)) continue;
        const s = segments[j], e = segments[j + 1];
        const last = line[line.length - 1];
        const first = line[0];

        if (near(last, s)) { line.push(e); used.add(j); extended = true; }
        else if (near(last, e)) { line.push(s); used.add(j); extended = true; }
        else if (near(first, s)) { line.unshift(e); used.add(j); extended = true; }
        else if (near(first, e)) { line.unshift(s); used.add(j); extended = true; }
      }
    }

    if (line.length >= 3) lines.push(line);
  }

  return lines;
}

/**
 * Generate GIS-style contour lines from well water elevation data.
 *
 * @param wells - Array of {lat, lon, waterElev}
 * @param gridRes - Grid resolution (default 40)
 * @param numLevels - Number of contour levels (default 10)
 * @param padding - Padding around well extent in degrees (default 0.005)
 */
export function generateContours(
  wells: WellElevation[],
  gridRes: number = 40,
  numLevels: number = 10,
  padding: number = 0.005,
): ContourLine[] {
  if (wells.length < 3) return [];

  const lats = wells.map(w => w.lat);
  const lons = wells.map(w => w.lon);
  const elevs = wells.map(w => w.waterElev);

  const minLat = Math.min(...lats) - padding;
  const maxLat = Math.max(...lats) + padding;
  const minLon = Math.min(...lons) - padding;
  const maxLon = Math.max(...lons) + padding;
  const minElev = Math.min(...elevs);
  const maxElev = Math.max(...elevs);

  if (maxElev - minElev < 0.5) return []; // flat — no contours

  // Build IDW grid
  const latArr: number[] = [];
  const lonArr: number[] = [];
  for (let i = 0; i < gridRes; i++) latArr.push(minLat + (maxLat - minLat) * i / (gridRes - 1));
  for (let j = 0; j < gridRes; j++) lonArr.push(minLon + (maxLon - minLon) * j / (gridRes - 1));

  const wellPts = wells.map(w => ({ x: w.lon, y: w.lat, val: w.waterElev }));
  const grid: number[][] = [];
  for (let i = 0; i < gridRes; i++) {
    grid[i] = [];
    for (let j = 0; j < gridRes; j++) {
      grid[i][j] = idw(lonArr[j], latArr[i], wellPts);
    }
  }

  // Generate contour levels
  const step = (maxElev - minElev) / (numLevels + 1);
  const roundTo = step > 10 ? 5 : step > 1 ? 1 : 0.5;
  const levels: number[] = [];
  for (let l = minElev + step; l < maxElev; l += step) {
    levels.push(Math.round(l / roundTo) * roundTo);
  }

  // Extract contours at each level
  const contours: ContourLine[] = [];
  for (const level of levels) {
    const segments = marchingSquaresLevel(grid, gridRes, gridRes, latArr, lonArr, level);
    const polylines = connectSegments(segments);
    for (const pts of polylines) {
      contours.push({ elevation: level, points: pts });
    }
  }

  return contours;
}
